import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import DynamicTextInput from "../../components/Inputs/DynamicTextInput";

import { focusAreaSchema } from "../../validators/focusAreaValidators";
import {
  createFocusArea,
  deleteFocusArea,
  getFocusAreas,
  updateFocusArea,
} from "../../services/focusAreaService";

const FocusArea = () => {
  const [focusAreas, setFocusAreas] = useState([]);
  const [filteredFocusAreas, setFilteredFocusAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [focusAreaToEdit, setFocusAreaToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [focusAreaToDelete, setFocusAreaToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadFocusAreas();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = focusAreas.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFocusAreas(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, focusAreas]);

  const loadFocusAreas = async () => {
    setLoading(true);
    try {
      const { data } = await getFocusAreas();
      setFocusAreas(data.data);
      setFilteredFocusAreas(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load Focus Areas");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (focusAreaToEdit) {
      onUpdateFocusArea(payload, resetForm);
    } else {
      const nonEmptyFocusAreas = payload?.focusAreas.filter(
        (focusArea) => focusArea?.name !== ""
      );

      if (nonEmptyFocusAreas.length <= 0) {
        toast.error("Focus Areas cannot be empty!");

        return;
      }
      onCreateFocusArea(nonEmptyFocusAreas, resetForm);
    }
  };

  const onCreateFocusArea = async (focusAreasArray, resetForm) => {
    setErrs(null);

    try {
      const promises = focusAreasArray.map(async (focusArea) => {
        const payload = { name: focusArea.name };

        const { data } = await createFocusArea(payload);

        return data;
      });

      const createdFocusAreas = await Promise.all(promises);

      setFocusAreas((prev) => [...prev, ...createdFocusAreas]);

      toast.success("Focus Area(s) created successfully");

      setIsModalOpen(false);

      resetForm({ values: { focusAreas: [{ name: "" }] } });

      await loadFocusAreas();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Some Focus Areas were not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditingBody) => {
    setFocusAreaToEdit(accreditingBody);
    setIsModalOpen(true);
  };

  const onUpdateFocusArea = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalFocusAreas = [...focusAreas];

      const focusAreaIndex = originalFocusAreas.findIndex(
        (focusArea) => focusArea.id === focusAreaToEdit.id
      );

      if (focusAreaIndex === -1) throw new Error("Focus Area not found.");

      const { data } = await updateFocusArea(focusAreaToEdit.id, payload);

      originalFocusAreas[focusAreaIndex] = data.data;

      setFocusAreas(originalFocusAreas);

      setFocusAreaToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Focus Area updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Focus Area not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setFocusAreaToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteFocusArea = async () => {
    const originalFocusAreas = [...focusAreas];

    const newFocusAreas = originalFocusAreas.filter(
      (focusArea) => focusArea.id !== focusAreaToDelete.id
    );

    setFocusAreas(newFocusAreas);

    try {
      await deleteFocusArea(focusAreaToDelete?.id);

      toast.success("Focus Area deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Focus Area not deleted, try again later"
        }`
      );

      setFocusAreas(originalFocusAreas);
    } finally {
      setIsDeleteModalOpen(false);

      setFocusAreaToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setFocusAreaToEdit(null);
    setFocusAreaToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 500,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Focus Area">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Focus Area">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Focus Areas"
        subtitle1="Institutions"
        subtitle2="Focus Areas"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Focus Areas List"
            count={filteredFocusAreas.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Focus Area"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredFocusAreas.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {focusAreaToEdit ? "Edit Focus Area" : "Add Focus Area"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            {focusAreaToEdit ? (
              <Formik
                initialValues={{
                  name: focusAreaToEdit ? focusAreaToEdit?.name : "",
                }}
                enableReinitialize
                validationSchema={focusAreaSchema}
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => {
                  return (
                    <Form className="space-y-5 px-4! py-4!">
                      {errs && <FormErrors errors={errs} />}

                      <TextInput
                        name="name"
                        title="Name"
                        placeholder="Enter Focus Area"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <div className="flex justify-end gap-2">
                        <FormCancelButton
                          text="Cancel"
                          onClick={handleCancel}
                        />
                        <FormButton
                          text="Save"
                          type="primary"
                          onClick={submitForm}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  focusAreas: [{ name: "" }],
                }}
                enableReinitialize
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => (
                  <Form className="space-y-2 !px-5 pb-3">
                    {errs && <FormErrors errors={errs} />}
                    <FieldArray name="focusAreas">
                      {({ remove, push }) =>
                        (values.focusAreas || []).map(
                          (accreditingBody, index) => (
                            <DynamicTextInput
                              key={index}
                              index={index}
                              name={`focusAreas.${index}.name`}
                              placeholder="Enter Focus Area Name"
                              requiredField={true}
                              errors={errors}
                              values={values}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              push={push}
                              remove={remove}
                              pushName="focusAreas"
                              dynamicFieldMap={{
                                auditCategories: {
                                  name: "",
                                },
                              }}
                            />
                          )
                        )
                      }
                    </FieldArray>
                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteFocusArea}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default FocusArea;
